import { datetime, type DatetimeType } from './datetime';
import { type Moment } from './moment-bridge';

// NOTE: Moment stays in the union because callers still hold one; createDatetime
// takes it at runtime, datetime()'s dayjs-derived signature does not.
type DateInput =
  | DatetimeType
  | Moment
  | string
  | number
  | Date
  | null
  | undefined;

const toDatetime = (date: DateInput): DatetimeType =>
  datetime(date as Exclude<DateInput, Moment>);

export const US_DATE_FORMAT = 'MM/DD/YYYY';
export const HUMAN_TIME_FORMAT = 'HH:mm';

const ISO_DATE_FORMAT = 'YYYY-MM-DD';
const US_COMPACT_DATE_FORMAT = 'M/D/YYYY';
const US_COMPACT_DATE_FORMAT_WITH_TIME_AND_TZ = 'M/D/YY, h:mmA z';
const US_HUMAN_DATE_TIME_WITH_TZ = 'MM/DD/YYYY [at] h:mma z';

export const toUSCompactDateFormat = (date: DateInput): string =>
  toDatetime(date).format(US_COMPACT_DATE_FORMAT);

export const toUSCompactDateFormatWithTimeAndTZ = (date: DateInput): string =>
  toDatetime(date).format(US_COMPACT_DATE_FORMAT_WITH_TIME_AND_TZ);

export const toISODateFormat = (date: DateInput): string =>
  toDatetime(date).format(ISO_DATE_FORMAT);

export const fromISODateFormat = (dateString: string): DatetimeType =>
  datetime(dateString, ISO_DATE_FORMAT, true);

export const toUSFormat = (date: DateInput): string =>
  toDatetime(date).format(US_DATE_FORMAT);

export const toHumanDateTimeWithTZ = (
  date: DateInput,
  timeZone = 'UTC'
): string =>
  toDatetime(date).tz(timeZone).format(US_HUMAN_DATE_TIME_WITH_TZ).trim();

export const toHumanTimeFormat = (date: DateInput): string =>
  toDatetime(date).format(HUMAN_TIME_FORMAT);

export const ifValidOrNow = (date: DateInput): DatetimeType => {
  const datetimeDate = toDatetime(date);
  return datetimeDate.isValid() ? datetimeDate : datetime();
};

export const formatToYearMonthDay = (date: DateInput): string | undefined => {
  if (!date) return;

  return toDatetime(date).format(ISO_DATE_FORMAT);
};

export function isPastDay(date: DateInput): boolean {
  const datePlusOneDay = toDatetime(date).add(1, 'day');
  return Boolean(date) && datePlusOneDay.isBefore(datetime());
}

// NOTE: Intl throws RangeError on a zone it does not recognize, and the supplier
// time_zone is validated only `if: active?` (Works::Institutions::Supplier).
function timeZoneAbbreviation(tz: string): string {
  try {
    return datetime().tz(tz).format('z');
  } catch {
    return '';
  }
}

// NOTE: `z` delegates to Intl, which carries English abbreviations only for the
// US zones and renders GMT±H for the rest; every zone assigned here is US or UTC.
export function humanizeTimezone(tz: string | null | undefined): string {
  if (!tz) return '';

  const offset = timeZoneAbbreviation(tz);
  const parsedTimezoneName = tz.split('/').join(' / ').replace(/_/g, ' ');

  return `${offset} - ${parsedTimezoneName}`;
}
